<script setup lang="ts">
import type { CheckboxRootEmits, CheckboxRootProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { computed } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { Check } from "lucide-vue-next"
import { CheckboxIndicator, CheckboxRoot, useForwardPropsEmits } from "reka-ui"
import { cn } from "@/lib/utils"

type CheckedState = boolean | "indeterminate"

const props = defineProps<CheckboxRootProps & {
  checked?: CheckedState | null
  defaultChecked?: CheckedState
  class?: HTMLAttributes["class"]
}>()
const emits = defineEmits<CheckboxRootEmits & {
  "update:checked": [value: CheckedState]
}>()

const delegatedProps = reactiveOmit(props, "class", "checked", "defaultChecked", "modelValue", "defaultValue")
const forwarded = useForwardPropsEmits(delegatedProps)
const resolvedValue = computed(() => props.checked ?? props.modelValue)
const resolvedDefaultValue = computed(() => props.defaultChecked ?? props.defaultValue)

function handleUpdate(value: CheckedState) {
  emits("update:modelValue", value)
  emits("update:checked", value)
}
</script>

<template>
  <CheckboxRoot
    v-slot="slotProps"
    data-slot="checkbox"
    v-bind="forwarded"
    :model-value="resolvedValue"
    :default-value="resolvedDefaultValue"
    @update:model-value="handleUpdate"
    :class="
      cn('peer border-input data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=checked]:border-primary focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive size-4 shrink-0 rounded-[4px] border shadow-xs transition-shadow outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
         props.class)"
  >
    <CheckboxIndicator
      data-slot="checkbox-indicator"
      class="grid place-content-center text-current transition-none"
    >
      <slot v-bind="slotProps">
        <Check class="size-3.5" />
      </slot>
    </CheckboxIndicator>
  </CheckboxRoot>
</template>
